import React, { Component } from "react";

import classnames from "classnames";
import { QuoteEmployeePriceBreakdownBody } from "EmployerApplication/Components/QuoteEmployeePriceBreakdown";
import isIntuit from "Intuit/isIntuit";
import { RecommendationSurveyCard } from "QuotePage/app/shared/RecommendationSurveyCard";
import EnrolledPolicyPlansLoader from "QuotePage/Components/EnrolledPolicyPlansLoader";
import { displayDoctorNetworkInfo } from "QuotePage/NetworkSizeConstants";
import { getPlanDeviationProps, getPlanRangeProps, getPathId, getShopMode, getMedicalPlanEnrolledId } from "QuotePage/Selectors";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Scrollspy from "react-scrollspy";

import Header from "Cobalt/Header";
import { FullScreenModal, ModalBody, ModalHeader } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import { Card, CardHeader, CardSection } from "Common/Card/";
import FinePrint from "Common/FinePrint";
import { Row, Col } from "Common/Grid";
import { getPlans, getPlanById } from "Common/PlanLoader/Selectors";
import { SizeSwitch } from "Common/Responsive";

import MobilePlanDetails from "./MobilePlanDetails";
import DoctorNetwork from "./planDetails/DoctorNetwork";
import PlanDetailsHeader from "./planDetails/Header";
import PlanAnalysis from "./planDetails/PlanAnalysis";
import Documents from "./planDetails/PlanDetailsDocuments";
import PlanOverview from "./planDetails/PlanOverview";
import SimilarPlans from "./planDetails/SimilarPlans";

import type { RouterHistory } from "react-router-dom";
import type { MedicalPlan, PlanRangeProps } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("PlanDetails");

type PlanDetailsProps = {
  plan: MedicalPlan;
  planRangeProps: PlanRangeProps;
  planDeviationProps: Record<string, number>;
  totalItems: number;
  inCart: boolean;
  hideCosts: boolean;
};

export class PlanDetailsDumb extends Component<PlanDetailsProps> {
  render() {
    const { plan, planRangeProps, planDeviationProps, totalItems, inCart, hideCosts } = this.props;

    const scrollspyItems = inCart ? ["planOverview", "finePrint"] : ["planOverview", "recommendation", "finePrint", "planAnalysis", "similarPlans"];

    if (!plan) {
      return <div>Loading</div>;
    }

    const currentClassName = isIntuit() ? bem("MenuItem", "intuit-active") : bem("MenuItem", "active");

    // The Scrollspy needs the huge negative offset because the last element
    // in the list doesn't fully reach the top of the viewport, and thus
    // doesn't trigger the menu item on the left to get highlighted.
    return (
      <Row className={bem()}>
        <Col md={2} className={bem("Menu")}>
          <Row>
            <CardSection>
              <Header level={7}>Jump To</Header>
              <BodyText size="ns-twelve">
                <Scrollspy items={scrollspyItems} currentClassName={currentClassName} rootEl=".view-details-feed" componentTag="div" offset={-200}>
                  <a
                    href="#planOverview"
                    className={classnames(bem("MenuItem"), {
                      [bem("MenuItem", "intuit")]: isIntuit(),
                    })}
                  >
                    Plan Overview
                  </a>
                  {!inCart && (
                    <a
                      href="#recommendation"
                      className={classnames(bem("MenuItem"), {
                        [bem("MenuItem", "intuit")]: isIntuit(),
                      })}
                    >
                      Recommendations
                    </a>
                  )}

                  <a
                    href="#finePrint"
                    className={classnames(bem("MenuItem"), {
                      [bem("MenuItem", "intuit")]: isIntuit(),
                    })}
                  >
                    Plan Details
                  </a>
                  {displayDoctorNetworkInfo(plan.state) && (
                    <a
                      href="#doctorNetwork"
                      className={classnames(bem("MenuItem"), {
                        [bem("MenuItem", "intuit")]: isIntuit(),
                      })}
                    >
                      Doctor Network
                    </a>
                  )}

                  <a
                    href="#costBreakdown"
                    className={classnames(bem("MenuItem"), {
                      [bem("MenuItem", "intuit")]: isIntuit(),
                    })}
                  >
                    Cost Breakdown
                  </a>

                  {!inCart && (
                    <a
                      href="#planAnalysis"
                      className={classnames(bem("MenuItem"), {
                        [bem("MenuItem", "intuit")]: isIntuit(),
                      })}
                    >
                      Plan Analysis
                    </a>
                  )}

                  {!inCart && (
                    <a
                      href="#similarPlans"
                      className={classnames(bem("MenuItem"), {
                        [bem("MenuItem", "intuit")]: isIntuit(),
                      })}
                    >
                      Compare Similar Plans
                    </a>
                  )}
                </Scrollspy>
              </BodyText>
            </CardSection>
            <Documents plan={plan} />
          </Row>
        </Col>
        <Col md={hideCosts ? 10 : 7} className="view-details-feed" p={4}>
          <PlanOverview plan={plan} />
          {!inCart && <RecommendationSurveyCard location="plan_details_popover" plan={plan} />}

          <FinePrint plan={plan} planDeviationProps={planDeviationProps} isMobile={false} />

          {displayDoctorNetworkInfo(plan.state) && <DoctorNetwork plan={plan} />}

          {!inCart && !hideCosts && (
            <Card p={0} id="costBreakdown">
              <CardHeader>Cost Breakdown</CardHeader>
              <CardSection>
                <QuoteEmployeePriceBreakdownBody plan={plan} />
              </CardSection>
            </Card>
          )}

          {!inCart && <PlanAnalysis plan={plan} planRangeProps={planRangeProps} totalItems={totalItems} />}

          {!inCart && <SimilarPlans plan={plan} />}
        </Col>
      </Row>
    );
  }
}

type PlanDetailsModalProps = {
  show: boolean;
  plan: MedicalPlan;
  inCart: boolean;
  role: string;
  shopMode: string;
  history: RouterHistory;
  isAlternateCart: boolean;
};

const PlanDetailsModal = ({ show, plan, inCart, role, shopMode, history, isAlternateCart, ...rest }: PlanDetailsModalProps) => {
  if (!plan) {
    // Loading, hopefully. This destroys the exit animation though. A better way
    // might be for the modal contents to "remember" the plan they were showing
    // when the URL changes, for the exit animation.
    return null;
  }
  const quotePageOnHide = () => {
    // remove plan details from url
    const { pathname } = history.location;
    const cutoffPoint = pathname.indexOf("/plan-details");
    history.push(pathname.substring(0, cutoffPoint));
  };
  return (
    <FullScreenModal show onHide={quotePageOnHide} className="more-details-modal">
      <SizeSwitch
        xs={<MobilePlanDetails plan={plan} onHide={quotePageOnHide} {...rest} />}
        md={
          <div>
            <ModalHeader
              bsClass={classnames({
                "modal-header": true,
                "modal-header--LeftCloseButton": !isIntuit(),
              })}
              key="header"
              closeButton
              onHide={quotePageOnHide}
            >
              <PlanDetailsHeader plan={plan} inCart={inCart} role={role} shopMode={shopMode} />
            </ModalHeader>
            <ModalBody key="body" style={{ paddingTop: 0, paddingBottom: 0 }} className="more-details-modal-body">
              <PlanDetailsDumb plan={plan} inCart={inCart} {...rest} />
            </ModalBody>
          </div>
        }
      />
    </FullScreenModal>
  );
};

const mapRouteStateToProps = (state, ownProps) => {
  const shoppingPlan = getPlanById(state, ownProps.match.params.id);
  const enrolledPlan = ownProps.enrolledPlans?.find((plan) => plan.id === ownProps.match.params.id);

  const hideCosts = !!enrolledPlan;
  const inCart = !!enrolledPlan;

  return {
    plan: shoppingPlan || enrolledPlan,
    hideCosts,
    inCart,
    totalItems: getPlans(state).length,
    planRangeProps: getPlanRangeProps(state),
    planDeviationProps: getPlanDeviationProps(state),
    role: getMedicalPlanEnrolledId(state) == getPathId(state) ? "enrolled" : "non-enrolled",
    shopMode: getShopMode(state),
  };
};

const ConnectedPlanDetails = withRouter(connect(mapRouteStateToProps)(PlanDetailsModal));

/**
 * This component is only used in the QuotePage App,
 * So loadding the enrolledPlans here to avoid obfuscation in the QuotePage App file.
 */
const HydratedPlanDetails = (props) => (
  <EnrolledPolicyPlansLoader>
    {({ medicalPlans: enrolledPlans }) => <ConnectedPlanDetails {...props} enrolledPlans={enrolledPlans} />}
  </EnrolledPolicyPlansLoader>
);

export default HydratedPlanDetails;
